import Papa from 'papaparse'
import { DatabaseData, DatabaseInfo, TableInfo, ColumnInfo, RowData } from '../types/database'

export class CSVService {
  static async parseCSV(file: File, delimiter: string = ','): Promise<DatabaseData> {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        delimiter: delimiter,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            reject(new Error(`CSVパースエラー: ${results.errors[0].message}`))
            return
          }

          const rows = results.data as any[]
          if (rows.length === 0) {
            reject(new Error('CSVファイルにデータがありません'))
            return
          }

          // 列情報を推測
          const columns: ColumnInfo[] = Object.keys(rows[0]).map(key => ({
            name: key,
            type: this.inferColumnType(rows, key),
            nullable: true,
            primaryKey: false
          }))

          const tableInfo: TableInfo = {
            name: 'data',
            columns,
            rowCount: rows.length
          }

          const info: DatabaseInfo = {
            type: delimiter === '\t' ? 'tsv' : 'csv',
            name: file.name,
            size: file.size,
            tables: [tableInfo]
          }

          const tableData: DatabaseData['tables'] = {
            data: {
              columns,
              rows: rows.map((row, index) => ({
                ...row,
                __rowIndex: index // 内部的な行インデックス
              }))
            }
          }

          resolve({ info, tables: tableData })
        },
        error: (error) => {
          reject(new Error(`CSV読み込みエラー: ${error.message}`))
        }
      })
    })
  }

  private static inferColumnType(rows: any[], columnName: string): string {
    const values = rows.map(row => row[columnName]).filter(val => val !== null && val !== undefined && val !== '')
    
    if (values.length === 0) return 'TEXT'

    // 数値型のチェック
    const numericValues = values.filter(val => !isNaN(Number(val)) && val !== '')
    if (numericValues.length === values.length) {
      const numbers = numericValues.map(Number)
      const hasDecimals = numbers.some(num => num % 1 !== 0)
      return hasDecimals ? 'REAL' : 'INTEGER'
    }

    // 日付型のチェック
    const dateValues = values.filter(val => {
      const date = new Date(val)
      return !isNaN(date.getTime())
    })
    if (dateValues.length === values.length) {
      return 'DATETIME'
    }

    return 'TEXT'
  }

  static exportCSV(data: DatabaseData, originalFileName: string): Blob {
    const tableName = Object.keys(data.tables)[0]
    const table = data.tables[tableName]
    
    if (!table || table.rows.length === 0) {
      throw new Error('エクスポートするデータがありません')
    }

    // ファイル拡張子に基づいてデリミタを決定
    const isTSV = originalFileName.toLowerCase().endsWith('.tsv')
    const delimiter = isTSV ? '\t' : ','

    // ヘッダー行
    const headers = table.columns.map(col => col.name)
    const csvRows = [headers.join(delimiter)]

    // データ行
    for (const row of table.rows) {
      const values = headers.map(header => {
        const value = row[header]
        // カンマや改行を含む場合はダブルクォートで囲む
        if (typeof value === 'string' && (value.includes(delimiter) || value.includes('\n') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value || ''
      })
      csvRows.push(values.join(delimiter))
    }

    const content = csvRows.join('\n')
    const mimeType = isTSV ? 'text/tab-separated-values' : 'text/csv'
    
    return new Blob([content], { type: mimeType })
  }
} 